<script lang="ts" setup>
import moment from 'moment'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import type { StrapiResp } from '~/models/Strapi'
import dateLocale from '~/assets/data/date-localization.json'
import locales from '~/assets/data/locales.json'

const emit = defineEmits(['setMeta'])
const strapiData = ref()
const bestLocation = ref()
const topCompanies = ref()
const recentlyAdded = ref()
const discoverJobs = ref()
const popularCareer = ref([
  { label: 'Full Time', key: 'full_time', total: 0, url: useGenerateSearchUrl('', '', 'full_time') },
  { label: 'Part Time', key: 'part_time', total: 0, url: useGenerateSearchUrl('', '', 'part_time') },
  { label: 'Remote', key: 'remote', total: 0, url: useGenerateSearchUrl('', '', '', 'remote') },
  { label: 'Freelance', key: 'freelance', total: 0, url: useGenerateSearchUrl('', '', 'freelance') },
  { label: 'Internship', key: 'internship', total: 0, url: useGenerateSearchUrl('', '', 'internship') },
])
const urgentlyNeeded = ref([
  { label: 'Marketing', total: 0, url: useGenerateSearchUrl('marketing') },
  { label: 'Sales', total: 0, url: useGenerateSearchUrl('sales') },
  { label: 'IT Engineer', total: 0, url: useGenerateSearchUrl('developer') },
  { label: 'Customer Service', total: 0, url: useGenerateSearchUrl('customer service') },
  { label: 'Human Resource', total: 0, url: useGenerateSearchUrl('human resource') },
])

getStrapiData()
getPopularBest()
getTopCompanies()
getRecentlyAdded()
getUrgentlyNeeded()

// GET STRAPI DATA
async function getStrapiData() {
  const { data, error } = await useStrapiAPI<StrapiResp>(`/search-page?populate=*&publicationState=live&locale=${useStrapiLocale(useGetSubdomain(), null)}`, {
    key: 'get_searchpage',
    initialCache: false,
  })
  if (!isEmpty(data.value)) {
    strapiData.value = data.value.data.attributes
    updateMeta()
    await getDiscoverJob()
  }
  else { throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true }) }
}

// GET DISCOVER JOBS
async function getDiscoverJob() {
  discoverJobs.value = strapiData.value.discover_jobs_company_list
  const payload = ref([])
  strapiData.value.discover_jobs_company_list.forEach((item) => {
    payload.value.push({
      country: useGetSubdomain(),
      term: item.label,
      sort: 'relevance',
      type: 'simple_title_with_similar_titles_search',
    })
  })
  const { data, error } = await useSearchApi('/jobs/search-count', {
    initialCache: false,
    method: 'POST',
    body: payload,
    key: 'get_discover_job',
  })

  watch(data, (newValue) => {
    if (!isEmpty(newValue)) {
      discoverJobs.value.forEach((item) => {
        const match = newValue.find(d => d.payload.term === item.label)
        if (match)
          item.total = match.result
      })
    }
  }, { immediate: true })
}

// GET POPULAR CAREER & BEST LOCATON
async function getPopularBest() {
  const { data, error } = await useSearchApi('/jobs/stats', {
    initialCache: false,
    method: 'POST',
    body: {
      country: useGetSubdomain(),
    },
    key: 'get_popular_best',
  })
  if (!isEmpty(data.value)) {
    const resp = data.value as { jobs_per_city: []; jobs_per_type: [] }
    bestLocation.value = resp.jobs_per_city
    popularCareer.value.forEach((item) => {
      const match = resp.jobs_per_type.find((d: any) => d.key === item.key)
      if (match)
        item.total = match.doc_count
    })
  }
}

// GET URGENTLY NEEDED
async function getUrgentlyNeeded() {
  const payload = ref([])
  urgentlyNeeded.value.forEach((item) => {
    payload.value.push({
      country: useGetSubdomain(),
      term: item.label,
      sort: 'relevance',
      type: 'simple_title_with_similar_titles_search',
    })
  })
  const { data, error } = await useSearchApi('/jobs/search-count', {
    initialCache: false,
    method: 'POST',
    body: payload,
    key: 'get_urgently_needed',
  })
  if (!isEmpty(data.value)) {
    urgentlyNeeded.value.forEach((item) => {
      const match = data.value.find((d: any) => d.payload.term === item.label)
      if (match)
        item.total = match.result
    })
  }
}

// GET TOP COMPANIES & RECENTLY ADDED
async function getTopCompanies() {
  const { data, error } = await useCompanyApi(`/job/companies/basic?limit=4&sort=random&showEmployerJobsCount=true&completePercentageMin=30&countryCode=${useGetSubdomain()}`, {
    key: 'get_top_companies',
    initialCache: false,
  })
  if (data.value)
    topCompanies.value = data.value
}

async function getRecentlyAdded() {
  const { data, error } = await useCompanyApi(`/job/companies/basic?limit=4&sort=latest&showEmployerJobsCount=true&completePercentageMin=30&countryCode=${useGetSubdomain()}`, {
    key: 'get_recently_added',
    initialCache: false,
  })
  if (data.value)
    recentlyAdded.value = data.value
}

function updateMeta() {
  const countryName = useTextCapitalize(locales.find(item => item.region === useGetSubdomain())?.label || '')
  const monthID = dateLocale[0]?.months[parseInt(moment().format('M')) - 1]
  const monthMY = dateLocale[1]?.months[parseInt(moment().format('M')) - 1]
  const metaTitle = ref('')
  const metaDesc = ref('')

  if (useGetSubdomain() === 'id') {
    metaTitle.value = 'Cari Lowongan Kerja Terbaru dan Perusahaan Terbaik di Indonesia | Epicareer'
    metaDesc.value = `Cek lowongan pekerjaan terbaru di bulan ${monthID} ${moment().format('YYYY')} dari berbagai perusahaan terbaik di Indonesia`
  }
  else if (useGetSubdomain() === 'my') {
    metaTitle.value = 'Cari Kerja Terkini & Syarikat Teratas d Malaysia | Epicareer'
    metaDesc.value = `Semak kekosongan jawatan terkini pada ${monthMY} ${moment().format('YYYY')} dari pelbagai syarikat teratas di Malaysia.`
  }
  else {
    metaTitle.value = `Search Latest Jobs & Top Companies in ${countryName} | Epicareer`
    metaDesc.value = `Check the latest job openings on ${moment().format('MMMM')} ${moment().format('YYYY')} from various top companies in ${countryName}.`
  }

  if (!isEmpty(strapiData.value?.faq_items)) {
    useSchemaOrg([{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': strapiData.value?.faq_items.map((faq: any) => ({
        '@type': 'Question',
        'name': faq.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': faq.answer
            .filter(block => block.type === 'paragraph' || block.type === 'list')
            .map((block) => {
              return (block.children || []).map((child) => {
                if (child.type === 'text')
                  return child.text || ''
                else if (child.type === 'link')
                  return child.children[0].text || ''
              }).join('')
            })
            .join('\n'),
        },
      })),
    }])
  }

  emit('setMeta', {
    title: metaTitle.value,
    desc: metaDesc.value,
  })
}
</script>

<template>
  <div v-if="!isEmpty(strapiData)" class="text-gray-900 mt-10 !px-0 pb-20 lg:pb-40">
    <div class="lg:max-w-4xl xl:max-w-5xl mx-auto">
      <div class="text-3xl md:text-5xl font-bold mb-2 text-center">
        {{ strapiData.title }}
      </div>
      <div class="<md:text-sm text-center text-gray-600 mb-10">
        {{ strapiData.description }}
      </div>

      <!-- 4 boxes -->
      <div class="grid md:grid-cols-2 gap-6 mb-10">
        <div class="bg-gray-50 rounded-lg p-4 md:p-6 pb-6 md:pb-10">
          <div class="text-xl md:text-2xl font-bold">
            {{ strapiData.popular_career_title }}
          </div>
          <div v-if="!isEmpty(popularCareer) && !popularCareer.some(e => e.total === 0 || e.total === undefined)" class="mt-6 bg-white">
            <div v-for="(item, idx) in popularCareer" :key="idx" class="p-4 border-b-1 border-gray-200 flex items-center">
              <a class="underline <md:w-[80%]" :href="item.url">
                {{ item.label }}
              </a>
              <span class="ml-auto font-semibold">{{ useCurrencyFormat(null, item.total, null, false, true, true) }} Jobs</span>
            </div>
          </div>
          <div v-else class="mt-6 text-[#5843E5] py-10 mx-auto >md:w-[65%] text-center grid">
            <Icon name="ic:twotone-work" class="w-18 h-18 mb-4 mx-auto" />
            <p class="text-lg font-bold mb-2 text-gray-900">
              Not many jobs to show
            </p>
            <p class="text-sm text-gray-600 mb-4">
              Sorry, there aren't many jobs in this category at the moment
            </p>
            <NuxtLink to="/jobs" class="font-bold flex items-center mx-auto">
              Visit job directory
              <Icon name="formkit:arrowright" class="w-4 h-4 ml-2" />
            </NuxtLink>
          </div>
        </div>
        <div class="bg-gray-50 rounded-lg p-4 md:p-6 pb-6 md:pb-10">
          <div class="text-xl md:text-2xl font-bold">
            {{ strapiData.urgently_needed_title }}
          </div>
          <div v-if="!isEmpty(urgentlyNeeded) && !urgentlyNeeded.some(e => e.total === 0 || e.total === undefined)" class="mt-6 bg-white">
            <div v-for="(item, idx) in urgentlyNeeded" :key="idx" class="p-4 border-b-1 border-gray-200 flex items-center">
              <a class="underline <md:w-[80%]" :href="item.url">
                {{ item.label }}
              </a>
              <span class="ml-auto font-semibold">{{ useCurrencyFormat(null, item.total, null, false, true, true) }} Jobs</span>
            </div>
          </div>
          <div v-else class="mt-6 text-[#5843E5] py-10 mx-auto >md:w-[65%] text-center grid">
            <Icon name="ic:twotone-work" class="w-18 h-18 mb-4 mx-auto" />
            <p class="text-lg font-bold mb-2 text-gray-900">
              Not many jobs to show
            </p>
            <p class="text-sm text-gray-600 mb-4">
              Sorry, there aren't many jobs in this category at the moment
            </p>
            <NuxtLink to="/jobs" class="font-bold flex items-center mx-auto">
              Visit job directory
              <Icon name="formkit:arrowright" class="w-4 h-4 ml-2" />
            </NuxtLink>
          </div>
        </div>
        <div class="bg-gray-50 rounded-lg p-4 md:p-6 pb-6 md:pb-10">
          <div class="text-xl md:text-2xl font-bold">
            {{ strapiData.best_location_title }}
          </div>
          <div v-if="!isEmpty(bestLocation) && !bestLocation.some(e => e.doc_count === 0 || e.doc_count === undefined)" class="mt-6 bg-white">
            <div v-for="(item, idx) in bestLocation" :key="idx" class="p-4 border-b-1 border-gray-200 flex items-center">
              <a class="underline <md:w-[80%]" :href="useGenerateSearchUrl('', item.key)">
                {{ item.key }}
              </a>
              <span class="ml-auto font-semibold">{{ useCurrencyFormat(null, item.doc_count, null, false, true, true) }} Jobs</span>
            </div>
          </div>
          <div v-else class="mt-6 text-[#5843E5] py-10 mx-auto >md:w-[65%] text-center grid">
            <Icon name="ic:twotone-explore" class="w-18 h-18 mb-4 mx-auto" />
            <p class="text-lg font-bold mb-2 text-gray-900">
              Not many jobs to show
            </p>
            <p class="text-sm text-gray-600 mb-4">
              Sorry, there aren't many jobs in this category at the moment
            </p>
            <NuxtLink to="/jobs" class="font-bold flex items-center mx-auto">
              Visit job directory
              <Icon name="formkit:arrowright" class="w-4 h-4 ml-2" />
            </NuxtLink>
          </div>
        </div>
        <div class="bg-gray-50 rounded-lg p-4 md:p-6 pb-6 md:pb-10">
          <div class="text-xl md:text-2xl font-bold">
            {{ strapiData.discover_jobs_title }}
          </div>
          <div v-if="!isEmpty(discoverJobs) && !discoverJobs.some(e => e.total === 0 || e.total === undefined)" class="mt-6 bg-white">
            <div v-for="(item, idx) in discoverJobs" :key="idx" class="p-4 border-b-1 border-gray-200 flex items-center">
              <a class="underline <md:w-[80%]" :href="`/companies/${item.label.toLowerCase().replaceAll(' ', '-')}`">
                {{ item.label }}
              </a>
              <span class="ml-auto font-semibold">{{ useCurrencyFormat(null, item.total, null, false, true, true) }} Jobs</span>
            </div>
          </div>
          <div v-else class="mt-6 text-[#5843E5] py-10 mx-auto >md:w-[65%] text-center grid">
            <Icon name="ic:twotone-business" class="w-18 h-18 mb-4 mx-auto" />
            <p class="text-lg font-bold mb-2 text-gray-900">
              Not many jobs to show
            </p>
            <p class="text-sm text-gray-600 mb-4">
              Sorry, there aren't many jobs in this category at the moment
            </p>
            <NuxtLink to="/companies/all" class="font-bold flex items-center mx-auto">
              Visit company directory
              <Icon name="formkit:arrowright" class="w-4 h-4 ml-2" />
            </NuxtLink>
          </div>
        </div>
      </div>

      <!-- top companies & recently added -->
      <div class="text-3xl md:text-4xl font-bold mb-2 md:mb-4 text-center pt-10">
        {{ strapiData.find_the_best_title }}
      </div>
      <div class="<md:text-sm text-center text-gray-600 mb-10">
        {{ strapiData.find_the_best_desc }}
      </div>
      <div class="mb-10 md:mb-20">
        <div class="flex items-center mb-6">
          <div class="text-xl md:text-2xl font-bold">
            {{ strapiData.top_companies_title }}
          </div>
          <a v-if="!isEmpty(topCompanies)" class="ml-auto font-semibold text-[#5843E5]" href="/companies/all">
            See All
          </a>
        </div>
        <div v-if="!isEmpty(topCompanies)" class="grid md:grid-cols-2 gap-2 md:gap-6">
          <CompaniesCompanyCardV1 v-for="(item, idx) in topCompanies" :key="idx" impression-source="search-job" :show-vacancy="true" :lite="true" :item="item" />
        </div>
        <div v-else class="bg-gray-50 text-[#5843E5] py-10 mx-auto text-center grid">
          <Icon name="ic:twotone-business" class="w-18 h-18 mb-4 mx-auto" />
          <p class="text-lg font-bold mb-2 text-gray-900">
            Not many companies to show
          </p>
          <p class="text-sm text-gray-600 mb-4">
            Sorry, there aren't many companies in this category at the moment
          </p>
          <NuxtLink to="/companies/all" class="font-bold flex items-center mx-auto">
            Visit company directory
            <Icon name="formkit:arrowright" class="w-4 h-4 ml-2" />
          </NuxtLink>
        </div>
      </div>
      <div class="mb-10 md:mb-20">
        <div class="flex items-center mb-6">
          <div class="text-xl md:text-2xl font-bold">
            {{ strapiData.recently_added_title }}
          </div>
          <a v-if="!isEmpty(recentlyAdded)" class="ml-auto font-semibold text-[#5843E5]" href="/companies/all">
            See All
          </a>
        </div>
        <div v-if="!isEmpty(recentlyAdded)" class="grid md:grid-cols-2 gap-2 md:gap-6">
          <CompaniesCompanyCardV1 v-for="(item, idx) in recentlyAdded" :key="idx" impression-source="search-job" :show-vacancy="true" :lite="true" :item="item" />
        </div>
        <div v-else class="bg-gray-50 text-[#5843E5] py-10 mx-auto text-center grid">
          <Icon name="ic:twotone-business" class="w-18 h-18 mb-4 mx-auto" />
          <p class="text-lg font-bold mb-2 text-gray-900">
            Not many companies to show
          </p>
          <p class="text-sm text-gray-600 mb-4">
            Sorry, there aren't many companies in this category at the moment
          </p>
          <NuxtLink to="/companies/all" class="font-bold flex items-center mx-auto">
            Visit company directory
            <Icon name="formkit:arrowright" class="w-4 h-4 ml-2" />
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- FAQ -->
    <div class="pt-10 md:pt-20">
      <div class="lg:max-w-2xl xl:max-w-3xl mx-auto">
        <div class="text-2xl md:text-4xl font-bold mb-10 text-center">
          {{ strapiData.faq_title }}
        </div>
        <div v-for="(item, idx) in strapiData.faq_items" :key="idx" class="bg-white px-2 md:px-6 py-4 border-b-1 border-gray-200">
          <Disclosure v-slot="{ open }" :default-open="false">
            <DisclosureButton class="flex items-center w-full cursor-pointer">
              <label class="font-bold text-gray-700 block text-left">{{ item.question }}</label>
              <span class="ml-auto flex-1 text-right">
                <Icon v-if="open" name="mdi:chevron-up" class="w-5 h-5" />
                <Icon v-else name="mdi:chevron-down" class="w-5 h-5" />
              </span>
            </DisclosureButton>
            <DisclosurePanel as="div" class="mt-4 text-gray-600 faq-list">
              <div v-for="(subitem, idx2) in item.answer" :key="idx2" v-html="useArticleHTMLSerializer(subitem)" />
            </DisclosurePanel>
          </Disclosure>
        </div>
        <div class="py-6 md:p-6 bg-white">
          <div class="font-sm mb-4">
            {{ strapiData.popular_search_title }}
          </div>
          <div class="grid md:grid-cols-2 gap-2 md:gap-6">
            <a v-for="(item, idx) in strapiData.popular_search_items" :key="idx" :href="item.url" class="py-2 px-4 rounded-full bg-gray-50 flex items-center text-[#5843E5]">
              {{ item.label }}
              <Icon name="heroicons:arrow-up-right-solid" class="w-4 h-4 ml-auto" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.faq-list :deep(ul){
  margin-left: 24px;
}
</style>
